import * as Phaser from "phaser"
import {
  FORCE_FIELD_ENABLED,
  FORCE_FIELD_X,
  GAME_HEIGHT,
  GAME_WIDTH,
  MAX_FALL_SPEED,
  SCENE_TUNING,
} from "../data/constants"
import type { GameData, GameSprite, PowerMode } from "../types"
import {
  HeroInvulRuntime,
  HeroMovementRuntime,
  type HeroMovementState,
  HeroPowerRuntime,
} from "./hero-state"
import type { VfxManager } from "./vfx-manager"

interface PlayerRuntimeOptions {
  scene: Phaser.Scene
  vfx: VfxManager
  getGameData: () => GameData
  getDogSprite: () => GameSprite
  onPlayerDamage: (
    damage: number,
    source?: "enemy" | "projectile" | "floor" | "generic",
  ) => void
  onDestroyThreatsTouchingPlayer: () => void
}

export class PlayerRuntime {
  private readonly options: PlayerRuntimeOptions
  private readonly movementRuntime = new HeroMovementRuntime("normal")
  private readonly powerRuntime = new HeroPowerRuntime()
  private readonly invulRuntime = new HeroInvulRuntime()

  constructor(options: PlayerRuntimeOptions) {
    this.options = options
  }

  public update(delta: number): void {
    const dogSprite = this.options.getDogSprite()
    const gameData = this.options.getGameData()
    const body = dogSprite.body as Phaser.Physics.Arcade.Body
    if (!body) return
    const isOverdrive = gameData.player.powerMode === "overdrive"

    body.setAllowGravity(!isOverdrive)

    // ── Update runtimes ───────────────────────────────────────────
    const time = this.options.scene.time.now
    this.movementRuntime.run(
      time,
      delta,
      gameData.player,
      dogSprite,
      this.options.vfx,
    )
    this.powerRuntime.run(
      time,
      delta,
      gameData.player,
      dogSprite,
      this.options.vfx,
      this.options.onDestroyThreatsTouchingPlayer,
    )
    this.invulRuntime.run(
      time,
      delta,
      gameData.player,
      dogSprite,
      this.options.vfx,
    )

    const homeX = GAME_WIDTH * SCENE_TUNING.playerHomeXRatio

    // ── Horizontal correction & damping ───────────────────────────
    const absVx = Math.abs(body.velocity.x)
    const launchBlend = Phaser.Math.Clamp((absVx - 200) / 200, 0, 1)
    const dampFactor = Phaser.Math.Linear(0.985, 0.998, launchBlend)
    const corrScale = Phaser.Math.Linear(1.0, 0.12, launchBlend)
    const correction =
      Phaser.Math.Clamp((homeX - dogSprite.x) * 0.025, -14, 14) * corrScale
    body.setVelocityX(
      Phaser.Math.Clamp(body.velocity.x * dampFactor + correction, -380, 480),
    )

    // ── Top boundary — elastic bounce ─────────────────────────────
    if (dogSprite.y < SCENE_TUNING.playerTopBoundaryY) {
      dogSprite.setY(SCENE_TUNING.playerTopBoundaryY)
      body.setVelocityY(Math.abs(body.velocity.y) * 0.78)
    }

    // ── Bottom boundary — elastic bounce ──────────────────────────
    if (dogSprite.y > SCENE_TUNING.playerBottomBoundaryY) {
      dogSprite.setY(SCENE_TUNING.playerBottomBoundaryY)
      body.setVelocityY(-Math.abs(body.velocity.y) * 0.78)
    }

    if (
      FORCE_FIELD_ENABLED &&
      dogSprite.x > FORCE_FIELD_X - 20 &&
      dogSprite.x < FORCE_FIELD_X + 20
    ) {
      if (dogSprite.x < FORCE_FIELD_X) {
        dogSprite.setX(FORCE_FIELD_X - 20)
        body.setVelocityX(-180)
      } else {
        dogSprite.setX(FORCE_FIELD_X + 20)
        body.setVelocityX(180)
      }
      this.options.vfx.shakeCamera(50, 0.004)
    }

    if (!isOverdrive && body.velocity.y > 0) {
      body.setVelocityY(
        Math.min(
          body.velocity.y + delta * SCENE_TUNING.fallAccelerationPerMs,
          MAX_FALL_SPEED * SCENE_TUNING.maxFallVelocityScale,
        ),
      )
    }

    if (isOverdrive) {
      const homeY = GAME_HEIGHT * 0.5
      const yCorrection = Phaser.Math.Clamp((homeY - dogSprite.y) * 0.06, -4, 4)
      const dampedVy = body.velocity.y * 0.58 + yCorrection
      body.setVelocityY(Phaser.Math.Clamp(dampedVy, -16, 16))
      body.setVelocityX(Phaser.Math.Linear(body.velocity.x, 0, 0.18))
    }

    let targetAngle = isOverdrive
      ? Phaser.Math.Clamp(body.velocity.y / 10, -6, 6)
      : Phaser.Math.Clamp(body.velocity.y / 20, -25, 25)

    if (!isOverdrive && body.velocity.y > -50 && body.velocity.y < 150) {
      targetAngle += Math.sin(this.options.scene.time.now / 200) * 6
    }

    dogSprite.angle = Phaser.Math.Linear(dogSprite.angle, targetAngle, 0.1)

    this.updateJetpackVisuals(dogSprite, body, isOverdrive)

    gameData.player.x = dogSprite.x
    gameData.player.y = dogSprite.y
  }

  public activatePowerMode(
    mode: Exclude<PowerMode, null>,
    duration: number,
  ): void {
    this.powerRuntime.activate(mode, duration)
  }

  public clearPowerMode(): void {
    this.powerRuntime.deactivate()
  }

  public setMovementStateOverride(state: HeroMovementState | null): void {
    const dogSprite = this.options.getDogSprite()
    const gameData = this.options.getGameData()
    this.movementRuntime.setOverrideState(state)
    this.movementRuntime.run(
      this.options.scene.time.now,
      0,
      gameData.player,
      dogSprite,
      this.options.vfx,
    )
  }

  private updateJetpackVisuals(
    dogSprite: GameSprite,
    body: Phaser.Physics.Arcade.Body,
    isOverdrive: boolean,
  ): void {
    const rad = Phaser.Math.DegToRad(dogSprite.angle)
    const cosR = Math.cos(rad)
    const sinR = Math.sin(rad)
    const nozzleX = dogSprite.x + cosR * -38 - sinR * 4
    const nozzleY = dogSprite.y + sinR * -38 + cosR * 4
    this.options.vfx.jetpackFlames.setPosition(nozzleX, nozzleY)

    const isRising = body.velocity.y < -50
    this.options.vfx.jetpackFlames.emitting = true
    this.options.vfx.jetpackFlames.frequency = isOverdrive
      ? 90
      : isRising
        ? 8
        : 40
    this.options.vfx.jetpackFlames.quantity = isOverdrive ? 1 : isRising ? 3 : 1
    this.options.vfx.jetpackFlames.scaleX = isOverdrive
      ? 0.35
      : isRising
        ? 1.2
        : 0.5
    this.options.vfx.jetpackFlames.scaleY = isOverdrive
      ? 0.35
      : isRising
        ? 1.2
        : 0.5
  }
}
